import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { toast } from '@/hooks/useToast'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreateApplicationDialog } from '@/components/dialogs/CreateApplicationDialog'
import { EditApplicationDialog } from '@/components/dialogs/EditApplicationDialog'
import { DeleteConfirmDialog } from '@/components/dialogs/DeleteConfirmDialog'

interface Application {
  id: number
  name: string
  app_key: string
  created_at: string
  settings: Record<string, unknown>
}

const SECRET_AUTO_HIDE_MS = 30_000

export default function ApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [applicationToEdit, setApplicationToEdit] = useState<Application | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [applicationToDelete, setApplicationToDelete] = useState<Application | null>(null)
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false)
  const [applicationToRegenerate, setApplicationToRegenerate] = useState<Application | null>(null)

  // Credential display states
  const [revealedSecrets, setRevealedSecrets] = useState<Record<number, string>>({})
  const [revealingId, setRevealingId] = useState<number | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const secretTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  const location = useLocation()

  useEffect(() => {
    fetchApplications()
  }, [])

  // Clear all revealed secrets on navigation away
  const clearAllSecrets = useCallback(() => {
    setRevealedSecrets({})
    Object.values(secretTimersRef.current).forEach(clearTimeout)
    secretTimersRef.current = {}
  }, [])

  useEffect(() => {
    return clearAllSecrets
  }, [location.pathname, clearAllSecrets])

  async function fetchApplications() {
    try {
      setLoading(true)
      const response = await api.get<Application[]>('/applications')
      setApplications(response.data)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message)
      } else {
        setError('Failed to fetch applications')
      }
    } finally {
      setLoading(false)
    }
  }

  async function copyToClipboard(text: string, fieldKey: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(fieldKey)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  async function handleRevealSecret(appId: number) {
    setRevealingId(appId)
    try {
      const response = await api.get<{ app_secret: string }>(`/applications/${appId}/secret`)
      setRevealedSecrets((prev) => ({ ...prev, [appId]: response.data.app_secret }))

      // Auto-hide after 30 seconds
      if (secretTimersRef.current[appId]) {
        clearTimeout(secretTimersRef.current[appId])
      }
      secretTimersRef.current[appId] = setTimeout(() => {
        setRevealedSecrets((prev) => {
          const next = { ...prev }
          delete next[appId]
          return next
        })
        delete secretTimersRef.current[appId]
      }, SECRET_AUTO_HIDE_MS)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        toast({
          title: 'Failed to reveal secret',
          description: err.message,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
    } finally {
      setRevealingId(null)
    }
  }

  function handleHideSecret(appId: number) {
    setRevealedSecrets((prev) => {
      const next = { ...prev }
      delete next[appId]
      return next
    })
    if (secretTimersRef.current[appId]) {
      clearTimeout(secretTimersRef.current[appId])
      delete secretTimersRef.current[appId]
    }
  }

  function handleOpenDeleteDialog(app: Application) {
    setApplicationToDelete(app)
    setDeleteDialogOpen(true)
  }

  async function handleDeleteApplication() {
    if (!applicationToDelete) return

    try {
      await api.delete(`/applications/${applicationToDelete.id}`)
      toast({
        title: 'Application deleted',
        description: `"${applicationToDelete.name}" has been deleted.`,
        variant: 'success',
      })
      fetchApplications()
    } catch (err) {
      if (err instanceof ApiRequestError) {
        toast({
          title: 'Failed to delete application',
          description: err.message,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
      throw err // Re-throw to keep dialog open
    }
  }

  function handleOpenEditDialog(app: Application) {
    setApplicationToEdit(app)
    setEditDialogOpen(true)
  }

  function handleEditDialogChange(open: boolean) {
    setEditDialogOpen(open)
    if (!open) {
      setApplicationToEdit(null)
    }
  }

  function handleDeleteDialogChange(open: boolean) {
    setDeleteDialogOpen(open)
    if (!open) {
      setApplicationToDelete(null)
    }
  }

  function handleOpenRegenerateDialog(app: Application) {
    setApplicationToRegenerate(app)
    setRegenerateDialogOpen(true)
  }

  function handleRegenerateDialogChange(open: boolean) {
    setRegenerateDialogOpen(open)
    if (!open) {
      setApplicationToRegenerate(null)
    }
  }

  async function handleRegenerateSecret() {
    if (!applicationToRegenerate) return

    try {
      const response = await api.post<{ app_secret: string }>(
        `/applications/${applicationToRegenerate.id}/regenerate-secret`,
        {}
      )
      // Show the new secret immediately
      setRevealedSecrets((prev) => ({
        ...prev,
        [applicationToRegenerate.id]: response.data.app_secret,
      }))

      // Set auto-hide timer for the new secret
      const appId = applicationToRegenerate.id
      if (secretTimersRef.current[appId]) {
        clearTimeout(secretTimersRef.current[appId])
      }
      secretTimersRef.current[appId] = setTimeout(() => {
        setRevealedSecrets((prev) => {
          const next = { ...prev }
          delete next[appId]
          return next
        })
        delete secretTimersRef.current[appId]
      }, SECRET_AUTO_HIDE_MS)

      toast({
        title: 'Secret regenerated',
        description: `New secret generated for "${applicationToRegenerate.name}". Make sure to copy it now.`,
        variant: 'success',
      })
    } catch (err) {
      if (err instanceof ApiRequestError) {
        toast({
          title: 'Failed to regenerate secret',
          description: err.message,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
      throw err // Re-throw to keep dialog open
    }
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Applications</h1>
          <p className="text-muted-foreground mt-2">
            Manage your applications and their payment configurations
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          Create Application
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : error ? (
        <p className="text-destructive">{error}</p>
      ) : applications.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-4">No applications yet</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              Create your first application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {applications.map((app) => (
            <Card key={app.id} className="transition-shadow hover:shadow-md">
              <CardHeader>
                <CardTitle>{app.name}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* App Key */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">App Key</p>
                  <div className="flex items-center gap-1">
                    <code className="flex-1 rounded bg-muted px-2 py-1 font-mono text-xs break-all">
                      {app.app_key}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 shrink-0 p-0"
                      onClick={() => copyToClipboard(app.app_key, `key-${app.id}`)}
                      title="Copy App Key"
                    >
                      {copiedField === `key-${app.id}` ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-green-600">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      )}
                    </Button>
                  </div>
                </div>

                {/* App Secret */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">App Secret</p>
                  {revealedSecrets[app.id] ? (
                    <div className="flex items-center gap-1">
                      <code className="flex-1 rounded bg-muted px-2 py-1 font-mono text-xs break-all">
                        {revealedSecrets[app.id]}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={() => copyToClipboard(revealedSecrets[app.id], `secret-${app.id}`)}
                        title="Copy App Secret"
                      >
                        {copiedField === `secret-${app.id}` ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-green-600">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                          </svg>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={() => handleHideSecret(app.id)}
                        title="Hide Secret"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="flex-1 rounded bg-muted px-2 py-1 font-mono text-xs tracking-widest text-muted-foreground">
                        ••••••••••••
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 shrink-0 px-2 text-xs"
                        onClick={() => handleRevealSecret(app.id)}
                        disabled={revealingId === app.id}
                        title="Reveal Secret"
                      >
                        {revealingId === app.id ? (
                          <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 h-3.5 w-3.5">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            Reveal
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Regenerate Secret */}
                <Button
                  variant="outline"
                  size="sm"
                  className="mb-4 w-full text-xs"
                  onClick={() => handleOpenRegenerateDialog(app)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 h-3.5 w-3.5">
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Regenerate Secret
                </Button>

                <p className="text-sm text-muted-foreground mb-4">
                  Created: {new Date(app.created_at).toLocaleDateString()}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/applications/${app.id}/payment-configs`}>
                      Payment Configs
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEditDialog(app)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleOpenDeleteDialog(app)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Application Dialog */}
      <CreateApplicationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchApplications}
      />

      {/* Edit Application Dialog */}
      {applicationToEdit && (
        <EditApplicationDialog
          open={editDialogOpen}
          onOpenChange={handleEditDialogChange}
          onSuccess={fetchApplications}
          application={applicationToEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={handleDeleteDialogChange}
        onConfirm={handleDeleteApplication}
        title="Delete Application"
        description={
          applicationToDelete
            ? `Are you sure you want to delete "${applicationToDelete.name}"? This action cannot be undone and will remove all associated payment configurations.`
            : ''
        }
      />

      {/* Regenerate Secret Confirmation Dialog */}
      <DeleteConfirmDialog
        open={regenerateDialogOpen}
        onOpenChange={handleRegenerateDialogChange}
        onConfirm={handleRegenerateSecret}
        title="Regenerate Secret"
        description={
          applicationToRegenerate
            ? `Are you sure you want to regenerate the secret for "${applicationToRegenerate.name}"? The current secret will be permanently invalidated. Any integrations using the old secret will stop working.`
            : ''
        }
        confirmText="Regenerate"
      />
    </div>
  )
}
